import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, ExternalLink, Info, Mail } from "lucide-react";
import { fetchCampaign, fetchAvailability, sendCampaignToOwner, formatApiError } from "@/lib/adminApi";
import { SmartEmailingPanel } from "./SmartEmailingPanel";

const API = process.env.REACT_APP_BACKEND_URL;

const copy = async (text, label) => {
  await navigator.clipboard.writeText(text);
  toast.success(`${label} zkopírováno do schránky.`);
};

const download = (html, spots) => {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `carspa-pozvanka-${spots}-mist.html`;
  a.click();
  URL.revokeObjectURL(a.href);
};

function TextCard({ title, text, testId }) {
  return (
    <div className="bg-brand-surface border border-brand-border p-5 flex flex-col gap-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">{title}</div>
        <button
          onClick={() => copy(text, title)}
          data-testid={`${testId}-copy`}
          className="text-xs inline-flex items-center gap-1.5 text-brand-accent hover:text-white transition-colors"
        >
          <Copy size={12} /> Kopírovat
        </button>
      </div>
      <p className="text-sm text-white/90 leading-relaxed whitespace-pre-wrap">{text}</p>
    </div>
  );
}

export function CampaignPanel() {
  const [spots, setSpots] = useState(null);
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const sendToOwner = async () => {
    setSending(true);
    try {
      const { to } = await sendCampaignToOwner(spots);
      toast.success(`Pozvánka odeslána na ${to}. Odtud ji přepošlete nebo vložte do newsletteru.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchAvailability()
      .then((a) => setSpots(Math.max(a.remaining, 1)))
      .catch(() => setSpots(20));
  }, []);

  useEffect(() => {
    if (spots == null) return;
    fetchCampaign(spots)
      .then(setData)
      .catch((err) => toast.error(formatApiError(err?.response?.data?.detail)));
  }, [spots]);

  return (
    <section className="space-y-4" data-testid="campaign-panel">
      <SmartEmailingPanel spots={spots ?? 20} />

      <div className="flex items-start gap-3 bg-brand-surface border border-brand-border p-4 text-sm text-white/80">
        <Info size={16} className="text-brand-accent shrink-0 mt-0.5" />
        <div>
          Hromadná pozvánka odchází přes <b>váš SmartEmailing</b> (odesílatel {"info@automyckacestlice.cz"}, odhlašovací odkaz a
          statistiky otevření najdete tam). Doporučení: nejdřív „Poslat pozvánku na můj e-mail“ pro kontrolu, pak
          „Odeslat pozvánku všem“. Počet míst níže se propisuje do předmětu i textu.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs uppercase tracking-[0.25em] text-brand-muted">Volných míst tento týden <span className="text-brand-accent normal-case tracking-normal">(předvyplněno živě)</span></label>
        <input
          type="number"
          min={1}
          max={99}
          value={spots ?? ""}
          onChange={(e) => setSpots(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
          data-testid="campaign-spots"
          className="w-20 h-9 bg-brand-bg border border-brand-border text-white text-sm px-3 focus:outline-none focus:ring-1 focus:ring-brand-accent"
        />
        <div className="flex-1" />
        <a
          href={`${API}/api/admin/campaign-preview?spots=${spots}`}
          target="_blank"
          rel="noreferrer"
          data-testid="campaign-preview"
          className="h-9 px-3 border border-brand-border hover:border-brand-accent hover:text-brand-accent transition-colors text-xs inline-flex items-center gap-2"
        >
          <ExternalLink size={13} /> Otevřít náhled
        </a>
        <button
          onClick={() => data && copy(data.html, "HTML pozvánky")}
          disabled={!data}
          data-testid="campaign-copy-html"
          className="h-9 px-3 border border-brand-border hover:border-brand-accent hover:text-brand-accent transition-colors text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Copy size={13} /> Kopírovat HTML
        </button>
        <button
          onClick={sendToOwner}
          disabled={!data || sending}
          data-testid="campaign-send-owner"
          className="h-9 px-3 border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-black transition-colors text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Mail size={13} /> {sending ? "Odesílám..." : "Poslat pozvánku na můj e-mail"}
        </button>
        <button
          onClick={() => data && download(data.html, spots)}
          disabled={!data}
          data-testid="campaign-download-html"
          className="h-9 px-3 bg-brand-accent text-black font-semibold hover:bg-brand-accentHover transition-colors text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Download size={13} /> Stáhnout HTML
        </button>
      </div>

      {data && (
        <>
          <div className="grid lg:grid-cols-2 gap-3">
            <TextCard title="Předmět e-mailu" text={data.subject} testId="campaign-subject" />
            <TextCard title="Preheader (náhledový text)" text={data.preheader} testId="campaign-preheader" />
            <TextCard title="Zpráva pro WhatsApp" text={data.whatsapp} testId="campaign-whatsapp" />
            <TextCard title="SMS (bez diakritiky)" text={data.sms} testId="campaign-sms" />
          </div>
          <div className="bg-brand-surface border border-brand-border">
            <div className="px-5 py-3 border-b border-brand-border text-[10px] uppercase tracking-[0.25em] text-brand-muted">
              Náhled pozvánky
            </div>
            <iframe
              title="Náhled pozvánky"
              srcDoc={data.html}
              data-testid="campaign-iframe"
              className="w-full h-[900px] bg-brand-bg"
            />
          </div>
        </>
      )}
    </section>
  );
}
